import React, { MouseEvent, ReactNode } from 'react';
import { RootState } from '../../../redux/store';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { FormicaProps, withFormica } from '../../../helpers/withFormica';
import { changeConnection, changeConnectionDirty, changeConnectionIsUpdate, clearConnection } from '../../../redux/actions/ConnectionActions';
import { enterComponent, leaveComponent } from '../../../redux/actions/ComponentActions';
import CustomEditorItemComponent from '../../custom/CustomEditorItemComponent';
import CustomTabv2 from '../../custom/CustomTabv2';
import ConnectionEditorComponent from './ConnectionEditorComponent';
import ConnectionAccessComponent from './ConnectionAccessComponent';
import ComponentName from '../../../constant/ComponentName';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../../constant/CollectionNames';
import { requireNonUndefined } from '../../../util/Assert';
import { InstanceContainer } from '../../../config/binding/InstanceBinding';
import Injectables from '../../../constant/Injectables';
import ServiceContainer from '../../../config/ServiceContainer';
import { ServiceNames } from '../../../constant/ServiceNames';
import { CommonConnectionModel } from '../../../domain/connection/ConnectionDomains';
import ValueFactory from '../../../util/ValueFactory';
import ConnectionService from '../../../service/ConnectionService';
import Breadcrumb from '../../Breadcrumb';
import { customConfirm } from '../../custom/CustomPopupConfirm';
import { confirmUndeployWithImpact } from '../../deploy/UndeployImpactDialog';
import LanguageUtil from '../../../util/LanguageUtil';
import RoleHelper from '../../../RoleHelper';
import ComponentAuditLog from '../../ComponentAuditLog';
import CustomLoading from '../../custom/CustomLoading';
import { clearPage } from 'redux/actions/PageActions';
import { ConnectionState } from '../../../redux/reducers/ConnectionReducer';
import StandardEditorContainerComponent, { StandardEditorContainerComponentArgs } from '../../../helpers/StandardEditorContainerComponent';
import { isStringValid } from '../../../util/Generator';
import MessageUtil from '../../../util/MessageUtil';
import { EnrichedComponentCategory } from '../../../domain/category/CategoryOperationDomains';
import eq = query.eq;
import { ConfirmPopup } from 'primereact/confirmpopup';

class ConnectionEditorContainer extends StandardEditorContainerComponent<Props, State, CommonConnectionModel> {
  public state = new State();

  private connectionSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly connectionService: ConnectionService = ServiceContainer.getInstance().get(ServiceNames.CONNECTION);

  constructor(props: Props) {
    const args: StandardEditorContainerComponentArgs = {
      canPreview: true,
      component: ComponentName.CONNECTION,
      icon: 'connection',
    };
    super(props, args);
  }

  public render(): ReactNode {
    const {
      connection: { connection },
      isUpdate,
    } = this.props;
    const { loading } = this.state;

    if (loading) {
      return <CustomLoading />;
    }

    return (
      connection != undefined && (
        <div className={'w-full h-full flex flex-col overflow-y-hidden '}>
          <Breadcrumb />
          <ConfirmPopup />
          <CustomEditorItemComponent
            name={connection.name}
            description={connection.description}
            status={connection.stage ? connection.stage : 'DRAFT'}
            isUpdate={isUpdate}
            component={ComponentName.CONNECTION}
            icon={'connection'}
            onChangeName={this.onChangeName}
            onChangeDescription={this.onChangeDescription}
            onSave={this.saveConnection}
            onDeploy={this.deployConnection}
            onUndeploy={this.undeployConnection}
            onRemove={this.removeConnection}
            disableSave={!RoleHelper.canEdit('Connection')}
            disableRemove={!RoleHelper.canEdit('Connection')}
            disableNameChange={!RoleHelper.canEdit('Connection')}
            disableDescriptionChange={!RoleHelper.canEdit('Connection')}
            disableDeploy={!RoleHelper.canDeploy('Connection')}
            disableUndeploy={!RoleHelper.canDeploy('Connection')}
          />
          <div className={'flex flex-col overflow-y-scroll h-full w-full py-3 '}>
            <CustomTabv2 tab={this.getTabs()} />
          </div>
        </div>
      )
    );
  }

  public async componentWillUnmount() {
    const { clearConnection } = this.props;
    clearConnection();
    super.componentWillUnmount();
  }

  protected _startTracker = async () => {
    const { changeConnectionIsUpdate, changeConnection } = this.props;
    const variableId = this.getItemIdFromRoute();
    if (variableId == undefined) {
      changeConnection(ValueFactory.initializeConnection());
      changeConnectionIsUpdate(false);
      this.setState({ loading: false });
    }
    const connectionQuery = query.QueryBuilder.select().from(CollectionNames.CONNECTIONS).where(eq('name', variableId));
    this.connectionSubscription = subscribe.Formica.subscribe(CollectionNames.CONNECTIONS, connectionQuery.build());
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.connectionSubscription]);
  };

  protected tracker = async () => {
    const { changeConnectionIsUpdate, changeConnection, changeConnectionDirty } = this.props;
    if (this.connectionSubscription?.ready()) {
      const connectionId = this.getItemIdFromRoute();
      if (connectionId == undefined) {
        changeConnectionIsUpdate(false);
        changeConnectionDirty(false);
        RoleHelper.setCurrentAccess('Edit');
        this.setState({ loading: false });
        return;
      }
      const connectionQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.CONNECTIONS).where(eq('name', connectionId)).build();
      const connection: CommonConnectionModel = (await this.repository.find(connectionQuery)) || ValueFactory.initializeConnection();
      changeConnection(connection);
      changeConnectionDirty(false);
      RoleHelper.setCurrentAccess(connection);
      this.setState({ loading: false });
    }
  };

  protected _stopTracker = async () => {
    await this.connectionSubscription?.stop();
    this.computation?.stop();
  };

  private onChangeName = (name: string) => {
    const {
      changeConnection,
      connection: { connection },
    } = this.props;
    if (isStringValid(name)) {
      connection.name = name.trim();
      changeConnection(connection);
    } else {
      MessageUtil.warning({
        summary: LanguageUtil.inappropriateCharacters(),
      });
    }
  };

  private onChangeDescription = (description: string) => {
    const {
      changeConnection,
      connection: { connection },
    } = this.props;
    connection.description = description;
    changeConnection(connection);
  };

  private saveConnection = async () => {
    const {
      connection: { connection },
      changeConnectionIsUpdate,
      isUpdate,
      lastCategory,
    } = this.props;
    const result = await this.connectionService.saveConnection(connection, isUpdate, lastCategory);
    if (result) {
      changeConnectionIsUpdate(true);
      if (!isUpdate) await this.switchToUpdate();
    }
  };

  private deployConnection = (e: MouseEvent<HTMLButtonElement>) => {
    const {
      connection: { connection },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        await this.connectionService.saveConnection(connection, true, {} as EnrichedComponentCategory);
        await this.connectionService.deployConnection(connection);
      },
      message: LanguageUtil.get({ id: 'messages.areyousuredeployconnection' }),
    });
  };

  private undeployConnection = (e: MouseEvent<HTMLButtonElement>) => {
    const {
      connection: { connection },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: () => confirmUndeployWithImpact(ComponentName.CONNECTION, [connection.name], () => this.connectionService.undeployConnection(connection)),
      message: LanguageUtil.get({ id: 'messages.confirm.deployConnect' }),
    });
  };

  private removeConnection = (e: MouseEvent<HTMLButtonElement>) => {
    const {
      connection: { connection },
      router: { navigate },
    } = this.props;
    customConfirm({
      target: e.currentTarget,
      accept: async () => {
        const success = await this.connectionService.removeConnection(connection.name);
        if (success) {
          navigate('/connection');
        }
      },
      message: LanguageUtil.get({ id: 'messages.confirm.removeConnect' }),
    });
  };

  protected getItemIdFromRoute = () => {
    const { changeConnectionIsUpdate } = this.props;
    const id = this.props.router.params.id;
    changeConnectionIsUpdate(id != undefined);
    return id;
  };

  private switchToUpdate = async () => {
    const {
      connection: { connection },
      router: { navigate, params },
      enterComponent,
    } = this.props;
    if (connection.name == '') return;
    enterComponent({ component: ComponentName.CONNECTION, id: connection.name });
    navigate(`/connection${params.folderId ? `/folder/${params.folderId}` : ''}/edit/${connection.name}`, { replace: true });
    setTimeout(() => {
      this.computation?.onStop(() => this.startTracker());
      this.stopTracker();
    });
  };

  protected getTabs = () => {
    return [
      {
        key: 'Connection',
        label: LanguageUtil.get({ id: 'common.tabs.connection', defaultMessage: 'Connection' }),
        content: <ConnectionEditorComponent />,
      },
      {
        key: 'Access',
        label: LanguageUtil.get({ id: 'common.tabs.access', defaultMessage: 'Access' }),
        content: <ConnectionAccessComponent />,
        visible: RoleHelper.canManage(ComponentName.CONNECTION, true),
      },
      {
        key: 'Audit',
        label: LanguageUtil.get({ id: 'common.tabs.audit', defaultMessage: 'Audit' }),
        content: <ComponentAuditLog component={ComponentName.CONNECTION} documentName={this.props.connection.connection.name} canPreview />,
        visible: RoleHelper.canManage(ComponentName.CONNECTION, true) && this.environmentUtil.isAuditLogVisible(),
      },
    ];
  };

  protected changeItem = (item: CommonConnectionModel) => {
    const { changeConnection } = this.props;
    changeConnection(item);
  };
}

interface Props extends ReduxProps, FormicaProps {}

class State {
  loading: boolean = true;
}

const mapStateToProps = (state: RootState) => ({
  connection: state.connection as ConnectionState,
  isUpdate: state.connection.isUpdate,
  lastCategory: state.page.lastCategory,
});
const mapDispatchToProps = {
  changeConnectionIsUpdate,
  changeConnection,
  clearConnection,
  enterComponent,
  leaveComponent,
  clearPage,
  changeConnectionDirty,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default withFormica(connector(ConnectionEditorContainer));
